import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { maskLine, stagger } from "@/lib/anim";
import { ArrowDownRight, ShieldCheck } from "lucide-react";

const headline = ["Stay calm.", "Stay yourself."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yBlobA = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const yBlobB = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] flex items-end overflow-hidden pb-16 md:pb-24 pt-28"
      data-testid="hero-section"
    >
      {/* Parallax aurora field */}
      <motion.div
        style={{ y: yBlobA }}
        className="aurora h-[38rem] w-[38rem] -top-40 -left-40 bg-primary/40"
        aria-hidden
      />
      <motion.div
        style={{ y: yBlobB }}
        className="aurora h-[30rem] w-[30rem] top-24 right-[-6rem] bg-accent/35"
        aria-hidden
      />
      <motion.div
        style={{ y: yBlobA }}
        className="aurora h-[22rem] w-[22rem] bottom-10 left-1/3 bg-primary/20"
        aria-hidden
      />

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 mx-auto max-w-7xl w-full px-6 md:px-12"
      >
        <motion.div
          variants={stagger(0.14)}
          initial="hidden"
          animate="show"
          className="max-w-4xl"
        >
          <div className="mask-line mb-6">
            <motion.p
              variants={maskLine}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-semibold text-primary"
              data-testid="hero-eyebrow"
            >
              <ShieldCheck className="h-4 w-4" strokeWidth={2} />
              A safe space, always open for you
            </motion.p>
          </div>

          <h1
            className="font-serif font-light tracking-tight text-foreground text-5xl sm:text-7xl lg:text-8xl leading-[0.98]"
            data-testid="hero-headline"
          >
            {headline.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span variants={maskLine} className="block">
                  {i === 1 ? (
                    <>
                      Stay <span className="italic text-primary">yourself.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mask-line mt-8">
            <motion.p
              variants={maskLine}
              className="max-w-xl text-lg md:text-xl leading-relaxed text-muted-foreground text-balance"
              data-testid="hero-subhead"
            >
              KelCalm lets students reach trained counsellors anonymously — through a
              secure, moderated messaging system — making sure support is never out of
              reach.
            </motion.p>
          </div>

          <motion.div
            variants={maskLine}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#about"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-base font-semibold text-primary-foreground hover:bg-primary/90 transition-colors duration-300"
            >
              Discover KelCalm
              <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </a>
            <a
              href="#vision"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center rounded-full border border-border bg-background/50 px-7 py-4 text-base font-semibold text-foreground hover:bg-secondary transition-colors duration-300"
            >
              Our vision
            </a>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
